import React, { Fragment } from 'react';
import { Link } from 'react-router';
import cn from 'classnames';
import CustomCell from 'chameleon/ui-stack/ui/assessments/CustomCell';
import AssessmentsTableRow from 'chameleon/ui-stack/ui/assessments/AssessmentsTableRow';
import dateTimeFormat from 'legacy-src/utility/DateTime';
import { renderPerformanceBar } from 'legacy-src/components/assessment/Results/utility';
import dayjs from 'dayjs';

import { isStudentSubmissionLate } from 'chameleon/ui-stack/ui/assessments/util';
import ScoredIcon from 'chameleon/ui-stack/ui/assessments/scored-icon';
import { StudentPracticeIcon } from 'legacy-src/utility';
import AssignmentTitle from 'legacy-src/components/assignment/AssignmentTitle';
import StudentVideoProgressBar from 'chameleon/ui-stack/ui/assessments/student-video-progress-bar';
import { sortAssessmentTitle } from '../util';
import StudentVideoStatus from '../student-video-status';
import A11y from 'chameleon/ui-stack/utilities/a11y';

import AssignmentTypeIcon from 'chameleon/ui-stack/ui/assessments/assignment-type-icon';
import Button from 'chameleon/ui-stack/ui/base/button';
import ChameleonIcon from 'chameleon/ui-stack/ui/base/icon';
import styles from '../styles.css';
import { SPP } from 'chameleon-src/components/assignments/constants';
import { get } from 'lodash';
import { SPPPerformanceBar } from 'chameleon/ui-stack/ui/assessments/spp-progress-bar';
import performanceBarStyles from 'chameleon/legacy/src/components/assessment/Results/StudentPerformanceBar/styles.css';
import useNavigation from 'chameleon/ui-stack/hooks/useNavigation';
import { parse } from 'query-string';

export const mobileColumnTitles = ({ id, isMobileStudentScoring }) => {
  const titlesById = {
    assignment: 'Assignment',
    time_limit: 'Time',
    start_date: 'Start',
    due_date: 'Due',
    submitted_at: 'Submitted',
    status: 'Status',
    action: 'Action',
  };

  return isMobileStudentScoring ? titlesById[id].toUpperCase() : titlesById[id];
};

const actionLabels = () => ({
  ...existingActions,
  score: 'Score',
  score__disabled: 'Score',
});

const getStatus = (status, progress, isOffline, isSPP, isPastDue) => {
  if (isOffline) {
    return {
      status: 'Locked',
    };
  }

  if (isPastDue) {
    return {
      status: 'Past Due',
      className: 'font-bold',
    };
  }
  const statusAndIcon = { status: '' };
  const {
    ready_to_score,
    scored,
    scoring,
    submitted,
    complete,
    started,
    student_scored,
    teacher_and_student_scored,
  } = progress;

  const submittedSPP = isSPP && scored && !started;

  if (status === 'opened') {
    statusAndIcon.status = 'Opened';
  }
  if (started) {
    statusAndIcon.status = 'In Progress';
  }
  if (status === 'locked' || status === 'closed') {
    statusAndIcon.status = 'Locked';
  }
  // SPP can not be scored at any case
  if (
    (scored || complete || student_scored || teacher_and_student_scored) &&
    !isSPP
  ) {
    statusAndIcon.status = 'Scored';
  }
  if (submitted || scoring || ready_to_score || submittedSPP) {
    statusAndIcon.status = 'Submitted';
  }
  return statusAndIcon;
};

const getAction = (action) => actionLabels()[action];

const startScore = ({ assignmentId, props, studentId }) => {
  const {
    router,
    location: { pathname, search },
  } = props;
  router.push({
    pathname: `/score/${assignmentId}/students/${studentId}`,
    query: { returnPath: `${pathname}${search}` },
  });
};

export const renderRow = ({
  row,
  onAssignmentClick,
  onReviewClick,
  removeReviewButton,
  isLdbEnabled,
  TableColumns,
  props,
  studentId,
  noDataPlaceholder,
  isMobileStudentScoring,
  showAssessmentPracticeIcon,
  hideOPSTabFlag,
  isMobile,
  isTablet,
}) => {
  const { original: assignment } = row || {};

  if (!assignment) {
    return null;
  }

  const {
    type,
    watched_percentage,
    resource_id: resourceId,
    unit_id: unitId,
    score,
    max_score: maxScore,
    filter_status: filterStatus,
    progress,
    status,
    print_toggle: isOffline,
    allow_late_submission: allowLateSubmission,
    timezone,
  } = assignment || {};
  const isVideo = type === 'video';
  const isSPP = type === 'spp';
  const isCompleted = filterStatus === 'completed';
  const isActive = filterStatus === 'assigned';
  const queryParams = parse(props?.location?.search ?? '');
  const isCompletedPage = get(queryParams, 'status', '') === 'completed';
  const clickableHyperlinkClass = 'text-blue-700 font-bold';

  // actions calculation
  let { actions } = assignment;
  if (!actions || actions.length === 0) {
    /**
     * completed videos don't have an action from the BE,
     * or an action column
     */
    if (isCompleted && isVideo) {
      actions = [''];
    } else {
      return '';
    }
  }

  // status calculation
  const isPastDue =
    isActive &&
    allowLateSubmission &&
    assignment.due_at &&
    dayjs(assignment.due_at)
      .utc(false)
      .tz(timezone)
      .isBefore(dayjs().utc(false).tz(timezone), 'minute');

  const { status: assignmentStatus, className: statusClassName = '' } =
    !isVideo &&
    getStatus(status, progress, isOffline, assignment.type === SPP, isPastDue);

  const action = !isVideo ? getAction(actions[0]) : existingActions[actions[0]];
  const hasScore = score >= 0 && maxScore > 0;
  const isAwaitingScoring =
    assignment.awaiting_scoring ||
    (progress?.ready_to_score > 0 &&
      (progress?.scored === 0 || progress?.teacher_and_student_scored === 0));
  const hasNoResults =
    !hasScore ||
    (progress?.scored === 0 &&
      progress?.complete === 0 &&
      progress?.started === 0 &&
      progress?.teacher_and_student_scored === 0 &&
      progress?.student_scored === 0 &&
      progress?.scoring === 0);
  const cantDisplayResults = !assignment.display_results;
  const isSubmittedSpp =
    get(assignment, 'item.is_spp', false) &&
    (assignmentStatus === 'Submitted' || assignmentStatus === 'Scored');
  let isDisabled =
    !isVideo &&
    ((!isCompleted && actions[0].includes('__disabled')) ||
      (isCompleted &&
        ((!isSPP &&
          (cantDisplayResults || isAwaitingScoring || hasNoResults)) ||
          (isSPP && !isSubmittedSpp))));
  const isReview = action === 'Review';
  const isScore = action === 'Score';
  const hideReviewButton = isReview && removeReviewButton;

  // timer calculation
  const { timer } = assignment;

  const startScoreProps = {
    props,
    assignmentId: assignment.id,
    studentId,
  };

  const openVideo = ({ props, videoUrl }) => {
    const { router } = props;
    const { queryParams, pushParamsToHistory } = useNavigation({ router });
    pushParamsToHistory({ ...queryParams, apd: videoUrl });
  };

  const studentScore = {
    learnosityReportMaxScore: maxScore,
    learnosityReportScoreParsed: score,
    percentCorrect:
      maxScore && (parseFloat(score) / parseFloat(maxScore)) * 100,
  };

  const getAssignmentTitle = () => {
    const { title, actions, is_secure: isSecure } = assignment;
    const isReviewDisabled = isReview && removeReviewButton;
    const SecureAssessment = isLdbEnabled && isSecure && (
      <div className={cn('mb-4', clickableHyperlinkClass)}>
        <b>Secure Assessment</b>
      </div>
    );
    const actionLabel = actions[0] && actions[0].replace('__disabled', '');
    const { params } = props || {};

    const resultAssignmentLink = () => {
      let titleText = '';

      if (assignment.is_final_exam || isVideo) {
        titleText = assignment.title;
      } else {
        titleText = assignment.item.title;
      }

      const title = (
        <AssignmentTitle
          rId={assignment.id}
          title={titleText}
          optionalTitle={assignment.title}
          isQB={!assignment.is_ppc}
          size="small"
          classes={{
            [clickableHyperlinkClass]: !isDisabled,
          }}
        />
      );

      if (isDisabled) {
        return title;
      }

      return (
        <Link
          className={clickableHyperlinkClass}
          to={`/${params.subject}/assessments/results/${assignment.id}`}
          onClick={(e) => onReviewClick(e, assignment)}
        >
          {title}
        </Link>
      );
    };

    const AssignmentLabel = ({ disabled, onClick }) => (
      <Fragment>
        {SecureAssessment}
        <div
          className={cn('assignment-title', {
            [clickableHyperlinkClass]: !disabled,
          })}
        >
          {title}
        </div>
        {!disabled && (
          <div>
            <Button onClick={onClick} className="mt-4" isOutline isLink>
              {`${actionLabel[0].toUpperCase()}${actionLabel.slice(1)}`}
            </Button>
          </div>
        )}
      </Fragment>
    );

    if (isVideo) {
      return (
        <Fragment>
          {!isDisabled ? (
            <Link
              onClick={() => openVideo({ props, videoUrl: assignment.url })}
            >
              <span className={cn('assignment_title', clickableHyperlinkClass)}>
                {title}
              </span>
            </Link>
          ) : (
            title
          )}
        </Fragment>
      );
    }

    if (isCompleted) {
      return (
        <span className="cell-with-popover">
          {resultAssignmentLink()}
          {!isVideo &&
            (showAssessmentPracticeIcon || hideOPSTabFlag) &&
            !!assignment.item.is_cb_practice_quiz && <StudentPracticeIcon />}
        </span>
      );
    }

    if (isMobileStudentScoring) {
      let onClick = () => {};
      let disabled = isDisabled;

      if (isReview) {
        onClick = () => onReviewClick(assignment);
        disabled = isReviewDisabled;
      } else if (isScore) {
        onClick = () => startScore(startScoreProps);
      } else if (isVideo) {
        onClick = () => openVideo({ props, videoUrl: assignment.url });
      } else {
        onClick = (e) => onAssignmentClick(e, assignment);
      }

      return <AssignmentLabel disabled={disabled} onClick={onClick} />;
    }

    if (isScore) {
      return (
        <Fragment>
          {!isDisabled ? (
            <Link onClick={() => startScore(startScoreProps)}>
              <span className={cn('assignment_title', clickableHyperlinkClass)}>
                {title}
              </span>
            </Link>
          ) : (
            title
          )}
        </Fragment>
      );
    }

    return (
      <Fragment>
        {SecureAssessment}
        {!isDisabled && !isReviewDisabled ? (
          <Link>
            <span className={cn('assignment_title', clickableHyperlinkClass)}>
              {title}
            </span>
          </Link>
        ) : (
          title
        )}
      </Fragment>
    );
  };

  const getPerformanceBar = () => {
    if (cantDisplayResults) {
      let text;
      if (!hasScore) {
        text = 'No results';
      } else {
        text = 'Results not made available by teacher';
      }
      return (
        <div className="rh__performance_wrap">
          <p className="rh__performance_label">
            <em>{text}</em>
          </p>
        </div>
      );
    }

    if (isAwaitingScoring) {
      return (
        <div className="rh__performance_wrap">
          <p className="rh__performance_label">
            <em>Awaiting Scoring</em>
          </p>
        </div>
      );
    }

    if (hasNoResults) {
      return (
        <div className="rh__performance_wrap">
          <p className="rh__performance_label">
            <em>No results</em>
          </p>
        </div>
      );
    }
    return (
      <div className="flex items-center">
        {renderPerformanceBar({ studentScore, progress, isTabbable: false })}
        <ScoredIcon
          teacherScored={progress.scored}
          studentScored={progress.student_scored}
          teacherAndStudentScored={progress.teacher_and_student_scored}
        />
      </div>
    );
  };

  const getSPPPerformanceBar = () => (
    <div
      className={cn(
        'flex items-center',
        performanceBarStyles.performanceBarMinWidth,
      )}
    >
      <SPPPerformanceBar
        spp={assignment}
        studentScore={studentScore}
        onClick={onClick}
      />
    </div>
  );

  // we create intermediate variables to make
  // the `onClick` conditional easier to read

  // when the assignment is a video
  const videoOnClick = () => openVideo({ props, videoUrl: assignment.url });

  const reviewOnClick = (e) => onReviewClick(e, assignment);

  const scoreOnClick = () => startScore(startScoreProps);

  const assignmentOnClick = (e) => onAssignmentClick(e, assignment);

  const studentSppNotStarted =
    !hasScore && isSPP && !(progress.not_started || progress.started);

  let onClickHandler = () => {};

  if (!isReview && !isCompleted) {
    onClickHandler = assignmentOnClick;
  }

  if ((isReview || isCompleted) && !studentSppNotStarted) {
    onClickHandler = reviewOnClick;
  }

  if (isScore && !isCompleted) {
    onClickHandler = scoreOnClick;
  }

  if (isVideo) {
    onClickHandler = videoOnClick;
  }

  const onClick = (e) => (isDisabled ? false : onClickHandler(e));

  const startsAt = assignment.starts_at
    ? dateTimeFormat(
        assignment.starts_at,
        'dateAndTimeShort',
        assignment.timezone,
      )
    : '';

  const dueAt = assignment.due_at
    ? dateTimeFormat(assignment.due_at, 'dateAndTimeShort', assignment.timezone)
    : '';

  const submittedAt = assignment.submitted_at
    ? dateTimeFormat(
        assignment.submitted_at,
        'dateAndTimeShort',
        assignment.timezone,
      )
    : '';

  const lineBreakRegEx = /\r?\n/;

  const [startsAtDate, startsAtHour] = startsAt.split(lineBreakRegEx);

  const [dueAtDate, dueAtHour] = dueAt.split(lineBreakRegEx);

  const [submittedAtDate, submittedAtHour] = submittedAt.split(lineBreakRegEx);

  const assignmentTitle = getAssignmentTitle(assignment);

  const isMobileResults = isMobile && isCompleted;

  const halfColumnStyling = isMobileResults
    ? 'w-1/2 flex flex-col items-center justify-center'
    : '';

  const hasResults = !isVideo && hasScore;

  const mobileFirstCellClass =
    isMobileResults && hasResults ? 'flex-1 ml-4' : 'flex';

  const renderResults = () => {
    if (isVideo) {
      return (
        <Fragment>
          <StudentVideoProgressBar
            id={row.id}
            watchedPercentage={watched_percentage}
          />
        </Fragment>
      );
    }
    if (isSPP) return getSPPPerformanceBar();
    return getPerformanceBar(assignment);
  };

  const cells = [
    CustomCell(
      `${assignment.id}-${1}`,
      assignment.type ? (
        <A11y>
          {/**
           * We want that when the cell is clicked, that we go to the same place as when the title
           * is clicked, but we don't need the double tabbing that happens since the inner
           * element is an anchor tag: https://academicmerit.atlassian.net/browse/FY-30286.
           * Do not add another tab index on the div here please.
           */}
          <div
            className={cn('items-center', mobileFirstCellClass)}
            onClick={onClick}
          >
            <AssignmentTypeIcon
              type={get(row, 'original.item.is_spp') ? SPP : assignment.type}
              showLine={isMobile || isTablet}
            />
            <span className="ml-4 flex-1 text-left">{assignmentTitle}</span>
            {isMobileResults && !isDisabled && hasResults
              ? renderResults()
              : null}
          </div>
        </A11y>
      ) : (
        assignmentTitle
      ),
      `${styles.table_cell} ${styles.title} title-cell`,
      false,
      { scope: 'row' },
      'th',
    ),
    CustomCell(
      `${assignment.id}-${2}`,
      <Fragment>
        {startsAt ? (
          <Fragment>
            <span className="text-black font-bold mr-2 block">
              {startsAtDate}
            </span>
            <span className="text-gray-700 font-normal">{startsAtHour}</span>
          </Fragment>
        ) : (
          noDataPlaceholder
        )}
      </Fragment>,
      `${styles.light_cell} ${styles.table_cell} starts-at-cell`,
    ),
    CustomCell(
      `${assignment.id}-${3}`,
      <Fragment>
        {dueAt ? (
          <Fragment>
            <span
              className={`text-black font-bold mr-2 ${
                isMobile && !isCompleted ? 'block' : ''
              }`}
            >
              {dueAtDate}
            </span>
            <span className="text-gray-700 font-normal">{dueAtHour}</span>
          </Fragment>
        ) : (
          noDataPlaceholder
        )}
      </Fragment>,
      `${styles.light_cell} ${styles.table_cell} ${halfColumnStyling} due-at-cell`,
    ),
    ...(isCompletedPage
      ? [
          CustomCell(
            `${assignment.id}-${8}`,
            <Fragment>
              {submittedAt ? (
                <Fragment>
                  <span
                    className={`text-black font-bold mr-2 ${
                      isMobile && !isCompleted ? 'block' : ''
                    }`}
                  >
                    {submittedAtDate}
                  </span>
                  <span className="text-gray-700 font-normal">
                    {submittedAtHour}
                  </span>

                  {isStudentSubmissionLate({
                    submitted: assignment.submitted_at,
                    customSetting: {},
                    dueAt: assignment.due_at,
                  }) && (
                    <span className="ml-1 font-medium text-red-600">
                      (Late)
                    </span>
                  )}
                </Fragment>
              ) : (
                noDataPlaceholder
              )}
            </Fragment>,
            `${styles.light_cell} ${styles.table_cell} ${halfColumnStyling} submitted-at-cell`,
          ),
        ]
      : []),
    CustomCell(
      `${assignment.id}-${4}`,
      timer && !isSPP ? (
        <span className="timer-cell">
          <ChameleonIcon name="timer" size="20px" role="presentation" />
          {`${timer} min`}
        </span>
      ) : (
        noDataPlaceholder
      ),
      `${styles.light_cell} ${styles.table_cell} timer-cell`,
      isMobile || isTablet, // isHidden
    ),
    CustomCell(
      `${assignment.id}-${5}`,
      isVideo ? (
        <StudentVideoStatus
          responseStatus={status}
          scope={[
            'units',
            `unit-${unitId}`,
            'resources',
            `resource-${resourceId}-subject-${props.params.subject}`,
          ]}
        />
      ) : (
        <div className={`assignment_status ${statusClassName}`}>
          {assignmentStatus}
        </div>
      ),
      `${styles.table_cell} status-cell`,
      isMobile, // isHidden
    ),
    ...(isCompleted
      ? [
          CustomCell(
            `${assignment.id}-${6}`,
            renderResults(),
            'results-cell',
            isMobile,
          ),
        ]
      : []),
    ...(!isCompleted
      ? [
          CustomCell(
            `${assignment.id}-${7}`,
            isVideo ? (
              <Button isOutline isLink disabled={isDisabled} onClick={onClick}>
                {action}
              </Button>
            ) : isScore ? (
              <Button isOutline isLink disabled={isDisabled} onClick={onClick}>
                {action}
              </Button>
            ) : (
              !hideReviewButton && (
                <Button isOutline isLink disabled={isDisabled}>
                  {action}
                </Button>
              )
            ),
            `${styles.light_cell} ${styles.table_cell} action-button-cell`,
            isMobile, // isHidden
          ),
        ]
      : []),
  ];

  return (
    <AssessmentsTableRow
      key={assignment.id}
      row={row}
      className="border-b border-gray-400 student-table-row"
      aria-label={assignment.title}
      onClick={!studentSppNotStarted && onClick}
      isDisabled={isDisabled}
    >
      <TableColumns cells={cells} />
    </AssessmentsTableRow>
  );
};

export const existingActions = {
  continue: 'Continue',
  begin: 'Begin',
  review: 'Review',
  begin__disabled: 'Begin',
  continue__disabled: 'Continue',
  view: 'View',
  view__disabled: 'View',
};

const baseHeaderCellStyle = 'w-1/7 text-base';

export const getHeaders = ({ isMobileStudentScoring, isResults }) => [
  {
    Header: isMobileStudentScoring
      ? mobileColumnTitles({ id: 'assignment', isMobileStudentScoring })
      : 'Assignment',
    className: 'pl-8 w-1/4 text-base assignment-name-th',
    accessor: 'title',
    sortType: sortAssessmentTitle(),
  },
  {
    Header: isMobileStudentScoring
      ? mobileColumnTitles({ id: 'start_date', isMobileStudentScoring })
      : 'Start Date',
    className: `${baseHeaderCellStyle} start-date-th`,
    id: 'starts_at',
    accessor(a) {
      const getDate = (a) => a.starts_at || a.startsAt || a.opened;
      return Number(new Date(getDate(a)));
    },
  },
  {
    Header: isMobileStudentScoring
      ? mobileColumnTitles({ id: 'due_date', isMobileStudentScoring })
      : 'Due Date',
    id: 'due_at',
    className: `${baseHeaderCellStyle} due-date-th`,
    accessor(a) {
      const getDate = (a) => a.due_at || a.dueAt;
      return Number(new Date(getDate(a)));
    },
  },
  ...(isResults
    ? [
        {
          Header: isMobileStudentScoring
            ? mobileColumnTitles({ id: 'submitted_at', isMobileStudentScoring })
            : 'Submitted Date',
          id: 'submitted_at',
          className: `${baseHeaderCellStyle} submitted-date-th`,
          accessor(a) {
            const getDate = (a) => a.submitted_at || a.submitted_at;
            return Number(new Date(getDate(a)));
          },
        },
      ]
    : []),
  {
    Header: `${
      isMobileStudentScoring
        ? mobileColumnTitles({ id: 'time_limit', isMobileStudentScoring })
        : 'Time Limit'
    }`,
    id: 'time_limit',
    className: `${baseHeaderCellStyle} time-limit-th`,
  },
  {
    Header: `${
      isMobileStudentScoring
        ? mobileColumnTitles({ id: 'status', isMobileStudentScoring })
        : 'Status'
    }`,
    id: 'status',
    className: `${baseHeaderCellStyle} status-th`,
  },
  ...(isResults
    ? [
        {
          Header: 'Results',
          id: 'results',
          className: cn(`${baseHeaderCellStyle} results-th`, {
            'results-col': !isMobileStudentScoring,
          }),
        },
      ]
    : []),
  ...(!isResults
    ? [
        {
          Header: `${
            isMobileStudentScoring
              ? mobileColumnTitles({ id: 'action', isMobileStudentScoring })
              : ''
          }`,
          id: 'action',
          className: 'pr-8 w-1/12 action-th',
        },
      ]
    : []),
];

export const getHiddenColumns = ({ isTablet, isMobile }) => {
  let hiddenColumns = [];

  if (isTablet) {
    hiddenColumns.push('time_limit');
  }

  if (isMobile) {
    hiddenColumns.push('status', 'action', 'performance', 'results');
  }

  return hiddenColumns;
};
